/**
 * Anti-forgery token resilience — global helpers, always loaded.
 * See docs/stories/antiforgery-token-auto-retry.md.
 *
 * The page bakes a (cookieToken:formToken) pair into GLOBAL.ANTIFORGERYTOKEN at render
 * time. That pair can go stale before the customer acts (identity change in another tab,
 * cookie eviction on mobile / iOS ITP, in-app browser cookie isolation). When it does, the
 * server rejects the POST with 400 and the action is silently lost. These helpers let the
 * AJAX layers recover by pulling a fresh pair and retrying, and surface a visible error if
 * recovery fails.
 *
 * Exposed on window (consumed by requestServiceMixin / dropZoneMixin, which load later):
 *   SP_refreshAntiforgeryToken()  -> Promise<string|null>  fresh "cookie:form", also stored
 *                                                           on GLOBAL.ANTIFORGERYTOKEN
 *   SP_showGlobalError(message?)  -> reveal the slim red bar (auto-hides; manually closable)
 *   SP_hideGlobalError()
 */
(function () {
	'use strict';

	var TOKEN_ENDPOINT = '/api/csrf/token';
	var REFRESH_INTERVAL_MS = 10 * 60 * 1000; // AC4: proactive refresh cadence for long-lived tabs
	var ERROR_BAR_AUTO_HIDE_MS = 8000;

	// --- Token refresh ----------------------------------------------------------------
	// Resolves to the fresh "cookie:form" pair (also written to GLOBAL.ANTIFORGERYTOKEN),
	// or null if the refresh failed. Never rejects — callers branch on null.
	function refreshAntiforgeryToken() {
		if (typeof axios === 'undefined') {
			return Promise.resolve(null);
		}

		return axios
			.get(TOKEN_ENDPOINT)
			.then(function (response) {
				var token = response && response.data && response.data.token;
				if (token && typeof GLOBAL !== 'undefined') {
					GLOBAL.ANTIFORGERYTOKEN = token;
				}
				return token || null;
			})
			.catch(function () {
				return null;
			});
	}

	// --- Global error bar (AC5) -------------------------------------------------------
	var autoHideTimer = null;

	function getErrorBar() {
		return document.getElementById('global-error-bar');
	}

	function hideGlobalError() {
		var bar = getErrorBar();
		if (bar) {
			bar.style.display = 'none';
		}
		if (autoHideTimer) {
			window.clearTimeout(autoHideTimer);
			autoHideTimer = null;
		}
	}

	function showGlobalError(message) {
		var bar = getErrorBar();
		if (!bar) {
			return;
		}

		if (message) {
			var textEl = bar.querySelector('.global-error-bar__message');
			if (textEl) {
				textEl.textContent = message;
			}
		}

		bar.style.display = '';

		if (autoHideTimer) {
			window.clearTimeout(autoHideTimer);
		}
		autoHideTimer = window.setTimeout(hideGlobalError, ERROR_BAR_AUTO_HIDE_MS);
	}

	document.addEventListener('DOMContentLoaded', function () {
		var bar = getErrorBar();
		if (!bar) {
			return;
		}
		var closeBtn = bar.querySelector('.global-error-bar__close');
		if (closeBtn) {
			closeBtn.addEventListener('click', function (e) {
				e.preventDefault();
				hideGlobalError();
			});
		}
	});

	// --- AC4: proactive refresh for long-lived tabs -----------------------------------
	// No-op when the tab is hidden (don't burn requests when the user isn't there), and
	// throttled so rapid tab in/out doesn't spam the endpoint.
	var lastProactiveRefresh = 0;

	function proactiveRefresh() {
		if (document.visibilityState === 'hidden') {
			return;
		}
		lastProactiveRefresh = Date.now();
		refreshAntiforgeryToken();
	}

	window.setInterval(proactiveRefresh, REFRESH_INTERVAL_MS);

	document.addEventListener('visibilitychange', function () {
		if (document.visibilityState !== 'visible') {
			return;
		}
		if (Date.now() - lastProactiveRefresh < REFRESH_INTERVAL_MS) {
			return;
		}
		proactiveRefresh();
	});

	// --- Expose -----------------------------------------------------------------------
	window.SP_refreshAntiforgeryToken = refreshAntiforgeryToken;
	window.SP_showGlobalError = showGlobalError;
	window.SP_hideGlobalError = hideGlobalError;
})();
